import { getPur, pageParams, PurClientConfig, readDecimal, readId, readObject, readPage, readText, requireId } from "./purRead";

// Read-only aggregated stock of one product per storage, from the shared
// core agg-stock endpoint used by the stock management screen. Quantities
// keep server precision as strings; absent fields stay null, never zero.
export interface PurProductStockRow {
  productId: number;
  code: string;
  name: string;
  productType: string;
  unitName: string;
  storageId: number | null;
  storageName: string;
  binLocation: string | null;
  totalQty: string | null;
  unexpQty: string | null;
  grandQty: string | null;
  minQty: string | null;
  maxQty: string | null;
  minExpiry: string | null;
  activeCount: number | null;
  activeFlag: number | null;
}

export const normalizePurProductStock = (value: unknown): PurProductStockRow => {
  const row = readObject(value);
  const product = readObject(row.product);
  const storageRaw = row.storage;
  const storage = storageRaw === null || storageRaw === undefined ? {} : readObject(storageRaw);
  const readCount = (input: unknown): number | null => {
    const decimal = readDecimal(input);
    return decimal === null ? null : Number(decimal);
  };
  return {
    productId: requireId(product.id),
    code: readText(product.code),
    name: readText(product.name),
    productType: readText(product.p_type_name),
    unitName: readText(product.unit_name),
    storageId: readId(storage.id),
    storageName: readText(storage.name),
    binLocation: readText(row.bin_location) || null,
    totalQty: readDecimal(row.total_qty),
    unexpQty: readDecimal(row.unexp_qty),
    // grand_qty is the product's not-expired total across every storage,
    // so the same value repeats on each row of one product.
    grandQty: readDecimal(row.grand_qty),
    minQty: readDecimal(row.min_qty),
    maxQty: readDecimal(row.max_qty),
    minExpiry: readText(row.min_exp) || null,
    activeCount: readCount(row.active_count),
    activeFlag: readCount(row.active_flag),
  };
};

export const listPurProductStock = async (
  client: PurClientConfig, query: { productId: number; limit: number; offset: number },
): Promise<{ count: number; records: PurProductStockRow[] }> => {
  const productId = readId(query.productId);
  if (!client.apiToken || readId(client.divisionId) === null || productId === null) {
    throw new Error("Invalid PUR product stock context");
  }
  const params = pageParams(query.limit, query.offset);
  params.set("product", String(productId));
  const data = await getPur({ ...client, pathPrefix: "/apis/core" }, `agg-stock/?${params}`);
  return readPage(data, normalizePurProductStock);
};
